import React, { useState, useMemo, useCallback } from 'react';
import Header from '../components/layout/Header';
import ErrorBanner from '../components/ui/ErrorBanner';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import JobStatusChart from '../components/charts/JobStatusChart';
import KPICard from '../components/ui/KPICard';
import { useJobs } from '../hooks/useJobs';
import { formatDate, formatCurrency, truncate, toLocalDateStr } from '../utils/formatters';
import { useBudget } from '../hooks/useForecast';
import apiClient from '../api/apiClient';
import { useBuFilter } from '../hooks/useBuMapping';
import ManagerFilter from '../components/ui/ManagerFilter';
import DateRangePicker from '../components/ui/DateRangePicker';

const STATUS_COLORS = {
  Scheduled: '#3b82f6', InProgress: '#f59e0b', 'On Hold': '#a855f7',
  Completed: '#22c55e', Canceled: '#ef4444', Hold: '#a855f7',
};

function defaultStart() { const d = new Date(); d.setDate(d.getDate() - 90); return toLocalDateStr(d); }
function defaultEnd() { const d = new Date(); d.setDate(d.getDate() + 1); return toLocalDateStr(d); }

function SortHeader({ label, sortKey, sortState, onSort, style }) {
  const active = sortState.key === sortKey;
  const arrow = active ? (sortState.dir === 'asc' ? ' \u25B2' : ' \u25BC') : '';
  return (
    <th style={{ ...style, cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort(sortKey)}>
      {label}{arrow}
    </th>
  );
}

export default function Jobs() {
  const [startDate, setStartDate] = useState(defaultStart());
  const [endDate, setEndDate] = useState(defaultEnd());
  const [managerId, setManagerId] = useState(null);
  const mgrOpts = useMemo(() => ({ managerId }), [managerId]);
  const { data: rawData, loading, error } = useJobs(startDate, endDate, mgrOpts);
  const handleDateChange = ({ start, end }) => { setStartDate(start); setEndDate(end); };
  const { budget } = useBudget(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState('all');
  const [buFilter, setBuFilter] = useState('all');
  const [search, setSearch] = useState('');
  const { filterJob, units } = useBuFilter();
  const [sort, setSort] = useState({ key: 'scheduled', dir: 'desc' });
  const [backfilling, setBackfilling] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState('');
  const runBackfill = useCallback(async () => {
    setBackfilling(true);
    setBackfillMsg('');
    try {
      await apiClient.post('/sync/job-metadata');
      setBackfillMsg('Backfill started — reload in ~30s to see updated fields.');
    } catch { setBackfillMsg('Backfill failed.'); }
    finally { setBackfilling(false); }
  }, []);

  const buNameMap = useMemo(() => {
    const m = {};
    for (const u of units) { if (u.st_business_unit_id) m[u.st_business_unit_id] = u.name; }
    return m;
  }, [units]);

  // Get unique BU names from data for dropdown
  const buNames = useMemo(() => {
    const names = new Set();
    rawData.forEach(j => {
      const name = buNameMap[j.businessUnitId];
      if (name) names.add(name);
    });
    return [...names].sort();
  }, [rawData, buNameMap]);

  const data = useMemo(() => {
    let filtered = rawData.filter(filterJob);
    if (buFilter !== 'all') {
      filtered = filtered.filter(j => buNameMap[j.businessUnitId] === buFilter);
    }
    return filtered;
  }, [rawData, filterJob, buFilter, buNameMap]);

  const filtered = data
    .filter(j => statusFilter === 'all' || j.status === statusFilter)
    .filter(j => !search || `${j.jobNumber || j.number || ''} ${j.summary || ''} ${j.customerName || ''} ${j.techName || ''}`.toLowerCase().includes(search.toLowerCase()));

  const statuses = [...new Set(data.map(j => j.status).filter(Boolean))];
  const completed = filtered.filter(j => j.status === 'Completed').length;
  const inProgress = filtered.filter(j => ['InProgress', 'Dispatched'].includes(j.status)).length;
  const totalRevenue = filtered.reduce((s, j) => s + (j.total || 0), 0);

  const handleSort = (key) => {
    setSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let va, vb;
      switch (sort.key) {
        case 'number': va = a.jobNumber || a.number || ''; vb = b.jobNumber || b.number || ''; break;
        case 'summary': va = a.summary || ''; vb = b.summary || ''; break;
        case 'type': va = a.type?.name || a.jobTypeName || ''; vb = b.type?.name || b.jobTypeName || ''; break;
        case 'customer': va = a.customerName || ''; vb = b.customerName || ''; break;
        case 'tech': va = a.techName || ''; vb = b.techName || ''; break;
        case 'bu': va = buNameMap[a.businessUnitId] || ''; vb = buNameMap[b.businessUnitId] || ''; break;
        case 'status': va = a.status || ''; vb = b.status || ''; break;
        case 'total': va = a.total || 0; vb = b.total || 0; break;
        case 'scheduled': va = a.completedOn || a.createdOn || a.scheduledDate || a.start || ''; vb = b.completedOn || b.createdOn || b.scheduledDate || b.start || ''; break;
        case 'location': va = a.location?.name || a.location?.address || ''; vb = b.location?.name || b.location?.address || ''; break;
        default: va = ''; vb = '';
      }
      if (typeof va === 'number') return sort.dir === 'asc' ? va - vb : vb - va;
      return sort.dir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
    return arr;
  }, [filtered, sort, buNameMap]);

  return (
    <div>
      <Header title="Jobs" />
      <div style={styles.content}>
        <ErrorBanner message={error} />

        <DateRangePicker onChange={handleDateChange} defaultPresetIdx={1} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '-0.5rem', flexWrap: 'wrap' }}>
          <ManagerFilter value={managerId} onChange={setManagerId} />
          <button onClick={runBackfill} disabled={backfilling} style={styles.backfillBtn}>
            {backfilling ? 'Backfilling...' : 'Backfill Job Data'}
          </button>
          {backfillMsg && <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{backfillMsg}</span>}
          {loading && <span style={styles.loadingHint}>Loading...</span>}
        </div>

        {budget.job_count_budget > 0 && (
          <div style={styles.budgetBar}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#374151' }}>Annual Job Count Budget</span>
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{data.length.toLocaleString()} / {budget.job_count_budget.toLocaleString()} ({budget.job_count_budget > 0 ? Math.round(data.length / budget.job_count_budget * 100) : 0}%)</span>
            </div>
            <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '999px', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: '999px', transition: 'width 0.4s', width: `${Math.min(100, Math.round(data.length / budget.job_count_budget * 100))}%`, background: data.length >= budget.job_count_budget ? '#22c55e' : '#f59e0b' }} />
            </div>
          </div>
        )}

        <div style={styles.kpiRow}>
          <KPICard title="Total Jobs" value={filtered.length} icon="🔧" color="#6366f1" loading={loading} />
          <KPICard title="Completed" value={completed} icon="✅" color="#22c55e" loading={loading} />
          <KPICard title="In Progress" value={inProgress} icon="⚡" color="#f59e0b" loading={loading} />
          <KPICard title="Total Job Value" value={formatCurrency(totalRevenue)} icon="💰" color="#3b82f6" loading={loading} />
        </div>

        <div style={styles.chartCard}>
          <h3 style={styles.sectionTitle}>Job Status Breakdown</h3>
          <JobStatusChart data={data} />
        </div>

        <div style={styles.tableCard}>
          <div style={styles.tableHeader}>
            <h3 style={styles.sectionTitle}>All Jobs ({sorted.length})</h3>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." style={styles.search} />
              <select value={buFilter} onChange={e => setBuFilter(e.target.value)} style={styles.select}>
                <option value="all">All Business Units</option>
                {buNames.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={styles.select}>
                <option value="all">All Statuses</option>
                {statuses.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {loading ? <LoadingSpinner /> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead><tr>
                  <SortHeader label="Job #" sortKey="number" sortState={sort} onSort={handleSort} style={styles.th} />
                  <SortHeader label="Customer" sortKey="customer" sortState={sort} onSort={handleSort} style={styles.th} />
                  <SortHeader label="Summary" sortKey="summary" sortState={sort} onSort={handleSort} style={styles.th} />
                  <SortHeader label="Type" sortKey="type" sortState={sort} onSort={handleSort} style={styles.th} />
                  <SortHeader label="Technician" sortKey="tech" sortState={sort} onSort={handleSort} style={styles.th} />
                  <SortHeader label="Business Unit" sortKey="bu" sortState={sort} onSort={handleSort} style={styles.th} />
                  <SortHeader label="Status" sortKey="status" sortState={sort} onSort={handleSort} style={styles.th} />
                  <SortHeader label="Total" sortKey="total" sortState={sort} onSort={handleSort} style={styles.th} />
                  <SortHeader label="Scheduled" sortKey="scheduled" sortState={sort} onSort={handleSort} style={styles.th} />
                  <SortHeader label="Location" sortKey="location" sortState={sort} onSort={handleSort} style={styles.th} />
                </tr></thead>
                <tbody>
                  {sorted.length === 0 ? (
                    <tr><td colSpan={10} style={styles.empty}>No jobs found</td></tr>
                  ) : sorted.map((job, i) => (
                    <tr key={job.id || i} style={i % 2 === 0 ? {} : { background: '#f8fafc' }}>
                      <td style={styles.td}>{job.jobNumber || job.number || '\u2014'}</td>
                      <td style={styles.td}>{truncate(job.customerName, 28) || '\u2014'}</td>
                      <td style={styles.td}>{truncate(job.summary, 35) || '\u2014'}</td>
                      <td style={styles.td}>{job.type?.name || job.jobTypeName || '\u2014'}</td>
                      <td style={styles.td}>{job.techName || '\u2014'}</td>
                      <td style={styles.td}><span style={styles.buTag}>{buNameMap[job.businessUnitId] || '\u2014'}</span></td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, background: (STATUS_COLORS[job.status] || '#94a3b8') + '20', color: STATUS_COLORS[job.status] || '#94a3b8' }}>
                          {job.status || '\u2014'}
                        </span>
                      </td>
                      <td style={styles.td}>{job.total ? formatCurrency(job.total) : '\u2014'}</td>
                      <td style={styles.td}>{formatDate(job.completedOn || job.createdOn || job.scheduledDate || job.start)}</td>
                      <td style={styles.td}>{truncate(job.location?.name || job.location?.address || '', 25) || '\u2014'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  content: { padding: '1.5rem 1.75rem', display: 'flex', flexDirection: 'column', gap: '1rem' },
  dateRow: { display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', background: '#fff', borderRadius: '0.75rem', padding: '0.75rem 1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  dateLabel: { display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.8rem', fontWeight: '600', color: '#475569' },
  dateInput: { padding: '0.375rem 0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '0.5rem', fontSize: '0.8rem', outline: 'none', color: '#1e293b' },
  resetBtn: { padding: '0.375rem 0.75rem', border: '1.5px solid #e2e8f0', borderRadius: '0.5rem', fontSize: '0.75rem', fontWeight: '600', color: '#6366f1', background: '#f8fafc', cursor: 'pointer' },
  loadingHint: { fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' },
  budgetBar: { background: '#fff', borderRadius: '0.75rem', padding: '0.75rem 1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e0e7ff' },
  kpiRow: { display: 'flex', gap: '1rem', flexWrap: 'wrap' },
  chartCard: { background: '#fff', borderRadius: '0.75rem', padding: '1.25rem 1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  tableCard: { background: '#fff', borderRadius: '0.75rem', padding: '1.25rem 1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  tableHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' },
  sectionTitle: { margin: '0 0 1rem 0', fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
  th: { padding: '0.625rem 0.75rem', background: '#f8fafc', color: '#64748b', fontWeight: '600', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '0.625rem 0.75rem', borderBottom: '1px solid #f1f5f9', color: '#1e293b', verticalAlign: 'middle' },
  badge: { padding: '0.2rem 0.625rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600' },
  buTag: { padding: '0.15rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '500', background: '#ede9fe', color: '#6366f1' },
  search: { padding: '0.4rem 0.625rem', border: '1.5px solid #e2e8f0', borderRadius: '0.5rem', fontSize: '0.8rem', outline: 'none', minWidth: '160px' },
  select: { padding: '0.4rem 0.625rem', border: '1.5px solid #e2e8f0', borderRadius: '0.5rem', fontSize: '0.8rem', outline: 'none' },
  empty: { padding: '2rem', textAlign: 'center', color: '#94a3b8' },
  backfillBtn: { padding: '0.35rem 0.75rem', border: '1.5px solid #6366f1', borderRadius: '0.5rem', fontSize: '0.75rem', fontWeight: '600', color: '#6366f1', background: '#f5f3ff', cursor: 'pointer' },
};
